/**
 * Flow Diagram Viewer Component
 * 
 * @deprecated This component is DEPRECATED and will be removed.
 * 
 * Mermaid diagrams are now rendered via the unified DocumentEditor:
 * - Created/stored as AeroDocuments with documentType: 'mermaid_diagram'
 * - Rendered client-side via TipTap VectorGraphicNode (mermaid.js)
 * - Opened in DocumentViewerContent like regular documents
 * 
 * This component remains for backward compatibility with legacy tabs.
 * New Mermaid diagrams use tabType: 'document' instead of tabType: 'mermaid'.
 * TODO: Remove in Phase 5 cleanup after testing confirms unified flow works.
 * 
 * ============================================================================
 * LEGACY DOCUMENTATION (for reference during migration):
 * ============================================================================
 * 
 * Displays Mermaid-generated flow diagrams (flowcharts, sequence diagrams,
 * state machines, ER diagrams) in a tab view with zoom/pan controls.
 * 
 * Reuses SVGImageViewer for the actual SVG display.
 */

import React, { useState, useEffect } from 'react';
import { logger } from '../../utils/logger';
import { SVGImageViewer } from './SVGImageViewer';
import { projectsApi, MermaidDiagram } from '../../services/api';
import type { MermaidDiagramType } from '../../types/panel-types';
import { MERMAID_TYPE_CONFIG } from '../../config/mermaid-templates';
import { Spinner, Button } from '@/components/ui';

interface FlowDiagramViewerProps {
  /** Title of the diagram */
  title: string;
  /** Mermaid diagram ID */
  mermaidDiagramId: string;
  /** Project ID */
  projectId: string;
  /** Pre-loaded SVG URL (optional - will fetch if not provided) */
  svgUrl?: string;
  /** Diagram type */
  mermaidType?: MermaidDiagramType;
  // AI Editing props (matching wiring diagram pattern)
  /** Whether AI editing is currently enabled for this flowchart */
  isAIEditing?: boolean;
  /** Whether currently linking to AI conversation */
  isLinking?: boolean;
  /** Callback to enable AI editing */
  onEnableAIEdit?: () => void;
  /** Callback to disable AI editing */
  onDisableAIEdit?: () => void;
  /** Version/timestamp to trigger refetch when AI updates the diagram */
  version?: number;
}

// Map mermaid types to display labels and colors
// Uses shared config from mermaid-templates.ts - auto-switches for dark mode

export const FlowDiagramViewer: React.FC<FlowDiagramViewerProps> = ({
  title,
  mermaidDiagramId,
  projectId,
  svgUrl: _initialSvgUrl,
  mermaidType,
  isAIEditing = false,
  isLinking = false,
  onEnableAIEdit,
  onDisableAIEdit,
  version,
}) => {
  const [svgUrl, setSvgUrl] = useState<string | null>(null);
  const [diagram, setDiagram] = useState<MermaidDiagram | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingRender, setPendingRender] = useState(false);

  // Fetch diagram data on mount and when version changes (AI updates)
  // This ensures we get fresh presigned URLs and updated content after AI generates new diagram
  useEffect(() => {
    const fetchDiagram = async () => {
      setLoading(true);
      setError(null);
      setPendingRender(false);
      try {
        logger.debug('[FlowDiagramViewer] Fetching diagram:', mermaidDiagramId, 'version:', version);
        const diagramData = await projectsApi.getMermaidDiagram(projectId, mermaidDiagramId);
        setDiagram(diagramData);
        if (diagramData.svgUrl) {
          setSvgUrl(diagramData.svgUrl);
        } else {
          // New diagram without SVG - show friendly pending state, not error
          setPendingRender(true);
        }
      } catch (err: unknown) {
        logger.error('[FlowDiagramViewer] Failed to fetch diagram:', err);
        setError((err as Error).message || 'Failed to load diagram');
      } finally {
        setLoading(false);
      }
    };

    fetchDiagram();
  }, [projectId, mermaidDiagramId, version]); // version triggers refetch when AI updates

  const displayType = mermaidType || diagram?.mermaidType || 'flowchart';
  const typeInfo = MERMAID_TYPE_CONFIG[displayType] || MERMAID_TYPE_CONFIG.flowchart;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header - matches EditorToolbar styling */}
      <div className="flex-shrink-0 flex items-center gap-3 px-3 py-2 bg-muted border-b border-border">
        {/* Title - takes available space */}
        <h2 className="text-sm font-medium text-foreground truncate flex-1">
          {title}
        </h2>
        
        {/* AI Editing toggle - matches wiring diagram pattern exactly */}
        {isAIEditing ? (
          <Button
            onClick={onDisableAIEdit}
            variant="secondary"
            size="sm"
            title="Stop AI editing and return to view-only mode"
          >
            <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            Stop Editing
          </Button>
        ) : (
          <Button
            onClick={onEnableAIEdit}
            disabled={isLinking}
            variant="info"
            size="sm"
            title="Enable AI editing to generate and modify flow diagrams"
          >
            {isLinking ? (
              <svg className="w-4 h-4 mr-1.5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" opacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
            ) : (
              <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            )}
            {isLinking ? 'Linking...' : 'Enable AI Editing'}
          </Button>
        )}
        
        {/* AI Linked indicator when editing */}
        {isAIEditing && (
          <span className="text-xs px-2 py-0.5 rounded bg-success-light text-success border border-success/30">
            AI Linked
          </span>
        )}

        {/* Type badges at end - like wiring diagram */}
        <span className={`px-2 py-0.5 text-xs rounded ${typeInfo.bgColor} ${typeInfo.textColor}`}>
          {typeInfo.label}
        </span>
        <span className="text-xs px-2 py-0.5 rounded border bg-transparent border-info/50 text-info">
          Flow Diagram
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="flex flex-col items-center gap-2">
              <Spinner size="xl" className="text-primary" />
              <span>Loading flow diagram...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-destructive gap-2">
            <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">{error}</span>
          </div>
        ) : pendingRender ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-info/10 flex items-center justify-center">
                <svg className="h-8 w-8 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">Diagram Ready for Generation</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This diagram has been created but doesn't have a visual rendering yet.
                Click "Enable AI Editing" to have AI generate the diagram visualization.
              </p>
              {!isAIEditing && onEnableAIEdit && (
                <Button
                  onClick={onEnableAIEdit}
                  disabled={isLinking}
                  variant="info"
                  size="default"
                >
                  {isLinking ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Linking...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                      Enable AI Editing
                    </>
                  )}
                </Button>
              )}
              {isAIEditing && (
                <p className="text-sm text-success">
                  AI is linked. Ask the AI to generate your diagram.
                </p>
              )}
            </div>
          </div>
        ) : svgUrl ? (
          <div className="h-full">
            <SVGImageViewer
              svgUrl={svgUrl}
              title={title}
              autoFitOnLoad={false}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <div className="text-4xl mb-4">📊</div>
              <div>No diagram to display</div>
            </div>
          </div>
        )}
      </div>

      {/* Footer info */}
      {diagram && (
        <div className="flex-shrink-0 px-4 py-2 bg-card border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Created: {new Date(diagram.createdAt).toLocaleDateString()}
              {diagram.createdBy && ` by ${diagram.createdBy}`}
            </span>
            {diagram.sourceDataRef?.diagramId && (
              <span>
                Source: {diagram.sourceDataRef.diagramId}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowDiagramViewer;
